import React, { useEffect, useState } from 'react';
import { zoom as zoomApi } from '@/utils/api';
import { Video, Save, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function ZoomPanel() {
  const [cfg, setCfg] = useState({ enabled: false, account_id: '', client_id_set: false, client_secret_set: false });
  const [form, setForm] = useState({ account_id: '', client_id: '', client_secret: '', enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const reload = () => zoomApi.getConfig().then(r => {
    setCfg(r.data);
    setForm({
      account_id: r.data.account_id || '',
      client_id: r.data.client_id_set ? '••••••••' : '',
      client_secret: r.data.client_secret_set ? '••••••••' : '',
      enabled: r.data.enabled,
    });
  });
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (body.client_id === '••••••••') delete body.client_id;
      if (body.client_secret === '••••••••') delete body.client_secret;
      await zoomApi.setConfig(body);
      toast.success('Saved');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { await zoomApi.test(); toast.success('Zoom connection OK'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Zoom test failed'); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="zoom-panel">
      <div>
        <h2 className="text-lg font-black mb-1 flex items-center gap-2" style={{ color: 'var(--ink)' }}>
          <Video size={18} /> Zoom (Server-to-Server)
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Connect your Zoom account so you can schedule client calls from anywhere in the app. Meetings are created under your Zoom account; clients just join via link.
        </p>
      </div>

      <div className="card-base p-4">
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--ink)' }}>
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({...form, enabled: e.target.checked})} data-testid="zoom-enabled" />
            Enable Zoom meetings
          </label>
          {cfg.enabled && cfg.account_id && cfg.client_id_set && cfg.client_secret_set ? (
            <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>Active</span>
          ) : cfg.enabled ? (
            <span className="badge-status" style={{ background: '#D9A44115', color: '#D9A441' }}>Incomplete</span>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Account ID</label>
            <input className="input-agency text-sm font-mono" value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value})} data-testid="zoom-account-id" placeholder="abcDEF123..." />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Client ID</label>
            <input className="input-agency text-sm font-mono" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} data-testid="zoom-client-id" />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase" style={{ color: 'var(--muted)' }}>Client Secret</label>
            <input className="input-agency text-sm font-mono" type="password" value={form.client_secret} onChange={e => setForm({...form, client_secret: e.target.value})} data-testid="zoom-client-secret" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button className="btn-primary text-sm flex items-center gap-1.5" onClick={save} disabled={saving} data-testid="save-zoom">
            <Save size={13} /> {saving ? 'Saving…' : 'Save'}
          </button>
          <button className="text-sm px-3 py-2 rounded-btn font-semibold" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }} onClick={test} disabled={testing || !cfg.enabled} data-testid="test-zoom">
            {testing ? 'Testing…' : 'Test connection'}
          </button>
        </div>
      </div>

      <div className="card-base p-4" style={{ background: '#E6F1F4' }}>
        <p className="text-xs font-bold uppercase mb-2" style={{ color: '#006080' }}>How to get these credentials</p>
        <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: 'var(--ink)' }}>
          <li>Go to <span className="font-mono">marketplace.zoom.us</span> → Build App → Server-to-Server OAuth → Create</li>
          <li>Name it (e.g., "Agency OS") and Activate</li>
          <li>In <b>App Credentials</b> copy: Account ID, Client ID, Client Secret</li>
          <li>Under <b>Scopes</b> add: <span className="font-mono">meeting:write</span>, <span className="font-mono">meeting:read</span>, <span className="font-mono">meeting:delete</span></li>
          <li>Paste the three values above → Enable ✓ → Save → Test connection</li>
        </ol>
        <a href="https://marketplace.zoom.us" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs mt-2 font-semibold" style={{ color: '#006080' }}>
          <ExternalLink size={11} /> Open Zoom Marketplace
        </a>
      </div>
    </div>
  );
}
